import { useNavigate } from 'react-router-dom';

import { CoachEducation, CoachEmployer, ICON_SIZES } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useGetCollegeByNameQuery } from '@backrs_ui/grow-ui/src/redux/college/college.api';
import { useGetEmployersByNameQuery } from '@backrs_ui/grow-ui/src/redux/employer/employer.api';

import { Avatar, Box, Chip, Stack, SxProps, Theme, Typography } from '@mui/material';

const COACH_PROFILE_STRINGS = appStrings.modules.profile.public.coachProfile;

type CoachEducationExperienceSectionProps = Readonly<{
  education?: CoachEducation[];
  employers?: CoachEmployer[];
}>;

export const CoachEducationExperienceSection = ({
  education,
  employers,
}: CoachEducationExperienceSectionProps) => {
  const navigate = useNavigate();

  const hasEducation = education && education.length > 0;
  const hasEmployers = employers && employers.length > 0;

  const employerNames = employers?.map((e) => e.name) || [];
  const { data: employerData } = useGetEmployersByNameQuery(employerNames, {
    skip: !hasEmployers,
  });

  const employersNotInDatabase =
    employers?.filter(
      (employer) =>
        !employerData?.find((e) => e.name.toLowerCase() === employer.name.toLowerCase()),
    ) || [];

  if (!hasEducation && !hasEmployers) {
    return null;
  }

  return (
    <Stack spacing={3}>
      {hasEducation && (
        <Box>
          <Typography
            variant="text-sm-semibold"
            color={({ backrs }) => backrs.colors['Colors/Text/text-brand-tertiary-alt']}
            sx={sectionTitleSx}
          >
            {COACH_PROFILE_STRINGS.education}
          </Typography>
          <Stack direction="column" flexWrap="wrap" gap={1}>
            {education.map((edu) => (
              <EducationChip key={edu.name} education={edu} />
            ))}
          </Stack>
        </Box>
      )}

      {hasEmployers && (
        <Box>
          <Typography
            variant="text-sm-semibold"
            color={({ backrs }) => backrs.colors['Colors/Text/text-brand-tertiary-alt']}
            sx={sectionTitleSx}
          >
            {COACH_PROFILE_STRINGS.experience}
          </Typography>
          <Stack direction="column" flexWrap="wrap" gap={1}>
            {/* Clickable employer chips (found in DB) */}
            {employerData?.map((employer) => (
              <Chip
                id={`coach-employer-chip-${employer.slug}`}
                key={employer.slug}
                label={employer.name}
                variant="tag"
                size="md"
                sx={chipSx(true)}
                onClick={() => navigate(`${APP_ROUTE.EMPLOYER}/${employer.slug}`)}
                avatar={
                  <Avatar src={employer.logo_media_url || '/images/employer-logo-default.svg'} />
                }
              />
            ))}

            {/* Non-clickable employer chips (not found in DB) */}
            {employersNotInDatabase.map((employer) => (
              <Chip
                key={employer.name}
                label={employer.name}
                variant="tag"
                size="md"
                sx={chipSx(false)}
                avatar={<Avatar src={employer.logo_url || '/images/employer-logo-default.svg'} />}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

type EducationChipProps = Readonly<{
  education: CoachEducation;
}>;

const EducationChip = ({ education }: EducationChipProps) => {
  const navigate = useNavigate();

  const { data: college } = useGetCollegeByNameQuery(education.name || '', {
    skip: !education.name,
  });

  return (
    <Chip
      id={`coach-education-chip-${college?.id || education.name?.toLowerCase().replace(/\s+/g, '-')}`}
      key={education.name}
      label={education.name}
      variant="tag"
      size="md"
      sx={chipSx(!!college?.id)}
      onClick={!college?.id ? undefined : () => navigate(`${APP_ROUTE.UNIVERSITY}/${college.id}`)}
      avatar={
        <Avatar
          src={
            college?.logo_media_url || education.logo_url || '/images/university-logo-default.svg'
          }
        />
      }
    />
  );
};

const sectionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  marginBottom: backrs.spacing['spacing-md'],
  display: 'block',
});

const chipSx =
  (clickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    backgroundColor: backrs.colors['Colors/Base/White'],
    cursor: clickable ? 'pointer' : 'default',
    '&:hover': {
      opacity: 0.8,
    },
    width: 'fit-content',
    '& .MuiChip-avatar': {
      marginLeft: `${backrs.spacing['spacing-none']} !important`,
      marginRight: `${backrs.spacing['spacing-xxs']} !important`,
      border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
      width: ICON_SIZES['16px'],
      height: ICON_SIZES['16px'],
      borderRadius: backrs.radius['radius-full'],
    },
  });
